import { useCallback, useRef } from "react";
import { questionAPI } from "../utils/api";

let banksCache = null;
let banksFetchPromise = null;
const questionsCache = new Map();

export function resetQuestionDataCache() {
  banksCache = null;
  banksFetchPromise = null;
  questionsCache.clear();
}

export function invalidateBanksCacheGlobal() {
  banksCache = null;
  banksFetchPromise = null;
}

export function invalidateBankQuestionsGlobal(bankId) {
  const id = String(bankId || "").trim();
  if (id) {
    questionsCache.delete(id);
  } else {
    questionsCache.clear();
  }
}

export function useQuestionDataCache() {
  const banksRef = useRef(banksCache);

  const fetchBanksOnce = useCallback(async () => {
    if (banksCache) {
      banksRef.current = banksCache;
      return banksCache;
    }
    if (banksFetchPromise) {
      return banksFetchPromise;
    }

    banksFetchPromise = questionAPI
      .getBanks()
      .then((res) => {
        const rows = res.data?.success ? res.data.data || [] : [];
        banksCache = rows;
        banksRef.current = rows;
        return rows;
      })
      .catch(() => [])
      .finally(() => {
        banksFetchPromise = null;
      });

    return banksFetchPromise;
  }, []);

  const fetchQuestions = useCallback(async (bankId) => {
    const id = String(bankId || "").trim();
    if (!id) return [];

    if (questionsCache.has(id)) {
      return questionsCache.get(id);
    }

    try {
      const res = await questionAPI.getQuestions(id);
      const rows = res.data?.success ? res.data.data || [] : [];
      questionsCache.set(id, rows);
      return rows;
    } catch {
      return [];
    }
  }, []);

  const invalidateBankQuestions = useCallback((bankId) => {
    const id = String(bankId || "").trim();
    if (id) questionsCache.delete(id);
  }, []);

  const appendBank = useCallback((bank) => {
    if (!bank) return;
    const next = [...(banksCache || []), bank];
    banksCache = next;
    banksRef.current = next;
  }, []);

  const appendQuestion = useCallback((bankId, question) => {
    const id = String(bankId || "").trim();
    if (!id || !question) return;
    const existing = questionsCache.get(id) || [];
    questionsCache.set(id, [...existing, question]);
  }, []);

  const getBankById = useCallback((bankId) => {
    const id = String(bankId || "").trim();
    return (banksCache || []).find((b) => String(b.ROWID) === id) || null;
  }, []);

  const removeBankFromCache = useCallback((bankId) => {
    const id = String(bankId || "").trim();
    if (!id) return;
    banksCache = (banksCache || []).filter((b) => String(b.ROWID) !== id);
    banksRef.current = banksCache;
    questionsCache.delete(id);
  }, []);

  const removeQuestionFromCache = useCallback((bankId, questionId) => {
    const bankKey = String(bankId || "").trim();
    const qid = String(questionId || "").trim();
    if (!bankKey || !qid) return;
    const existing = questionsCache.get(bankKey) || [];
    questionsCache.set(
      bankKey,
      existing.filter((q) => String(q.ROWID) !== qid)
    );
  }, []);

  const invalidateBanksCache = useCallback(() => {
    banksCache = null;
    banksFetchPromise = null;
    banksRef.current = null;
  }, []);

  return {
    fetchBanksOnce,
    fetchQuestions,
    invalidateBankQuestions,
    invalidateBanksCache,
    appendBank,
    appendQuestion,
    getBankById,
    removeBankFromCache,
    removeQuestionFromCache,
    getCachedBanks: () => banksCache,
  };
}
